import qs from "qs";
import { useContext, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import {
  CheckoutConfigurationType,
  LocalStorageKeys,
  TranslationContext,
} from "@is/constants";
import { ViewStateContext } from "@is/contexts";
import { useManageCheckoutConfigurations } from "@is/hooks/checkout";
import { LocalStorageService } from "@is/services";
import { rememberQRCheckoutSession } from "@is/utils/session";

const useManageQRCodeModal = () => {
  const { checkoutConfigurations } = useManageCheckoutConfigurations();

  const { t } = useTranslation(TranslationContext.Basket);

  const { qrCodeCheckout } = useContext(ViewStateContext).content;

  const qrCodeCheckoutConfiguration = useMemo(
    () =>
      checkoutConfigurations?.find(
        (configuration) =>
          configuration.type === CheckoutConfigurationType.QRCode
      ),
    [checkoutConfigurations]
  );

  const sessionId = LocalStorageService.getItem(LocalStorageKeys.SessionId);

  // The QR the shopper carries away is minted for THIS session; the websocket
  // that reports their payment is device-scoped, so the correlation has to be
  // kept here (ticket aecd58ce). Gated on the QR configuration for the same
  // reason as the inline basket QR: only a rendered QR may claim the slot.
  useEffect(() => {
    if (!qrCodeCheckoutConfiguration) {
      return;
    }

    rememberQRCheckoutSession(sessionId);
  }, [qrCodeCheckoutConfiguration, sessionId]);

  const qrCodeUrl = useMemo(() => {
    const languageCode = LocalStorageService.getItem(
      LocalStorageKeys.LanguageCode
    );
    const tenantId = LocalStorageService.getItem(LocalStorageKeys.TenantId);

    const queryString = qs.stringify({
      tenantId,
      languageCode,
      sessionId,
      checkoutConfigurationId: qrCodeCheckoutConfiguration?.id,
    });

    return `${window.location.origin}/instore/forward-basket?${queryString}`;
  }, [qrCodeCheckoutConfiguration, sessionId]);

  return {
    qrCodeUrl,
    inProgress: qrCodeCheckout.inProgress,
    t,
  };
};

export default useManageQRCodeModal;
